import React, {
  useState,
  useCallback,
  useMemo,
  useContext,
  useEffect,
} from 'react';
import moment from 'moment';
import format from 'string-template';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { PropTypes } from 'prop-types';
import { Tooltip } from '@mui/material';

import { ChatChannelListContext } from '../../../../../chat';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import Modal from '../../../../../components/v2/Modal';
import {
  PrimaryButton,
  CancelButton,
} from '../../../../../components/Button/ActionButtons';
import { CoachingActivity } from '../../../../../utils/log';
import useLogger from '../../../../../hooks/useLogger';
import useToast from '../../../../hooks/useToast';
import { ChannelClaimedFrom } from '../../../../utils/chatChannel';
// These are the titles of the activity feed sidebar menu
import sidebarTitles from '../../../../context/FeedContext/config/texts';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import feedType from '../../../../context/FeedContext/config/feedTypes';
import FeedContext from '../../../../context/FeedContext';

import { ClaimButton, CircularProgressContainer } from './styles';
import texts from './texts.json';

const CLAIM_TIMEOUT_MINS = 30;

const ClaimActivity = ({
  activity,
}) => {
  const {
    userDoc: {
      name: authUserName,
    },
    userId: authUserId,
  } = useContext(LoggedInUserContext);

  const {
    feedActivityList,
    claimedChannelMap,
    claimChannel,
    unclaimChannel,
  } = useContext(FeedContext);
  const { isReady: isChannelListReady } = useContext(ChatChannelListContext);

  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const { logCoachingActivity } = useLogger();

  const [reclaim, setReclaim] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [isClaimButtonDisabled, setIsClaimButtonDisabled] = useState(true);

  const {
    claimedAt,
    claimedBy,
    claimedFrom,
    claimedByUserName,
    user,
    type,
  } = activity;

  useEffect(() => {
    if (isComponentMountedRef.current && isChannelListReady) {
      setIsClaimButtonDisabled(false);
    }
  }, [isComponentMountedRef, isChannelListReady]);

  const isClaimedByUser = useMemo(() => claimedBy === authUserId, [
    claimedBy,
    authUserId,
  ]);

  const hasClaimedOne = useMemo(
    () => Array.from(claimedChannelMap.entries()).some(
      ([userId, channel]) => channel.claimedBy === authUserId && feedActivityList.some(
        (feedActivity) => feedActivity.type === feedType.CHECK_IN && feedActivity.user === userId,
      ),
    ),
    [claimedChannelMap, authUserId, feedActivityList],
  );

  const claimedTime = useMemo(() => (
    claimedAt ? moment.utc(claimedAt).local().fromNow() : null
  ), [claimedAt]);

  const claimActivity = useCallback(async () => {
    try {
      if (reclaim) {
        setIsLoading(true);
      }

      setIsClaimButtonDisabled(true);
      await claimChannel(user, authUserName, reclaim);
      showToast(texts.claimSuccessful);

      const [activityId] = activity.id;
      logCoachingActivity(CoachingActivity.CLAIMED_ACTIVITY, {
        activityId,
        activityType: type,
        clientId: user,
      });
      if (isComponentMountedRef.current) {
        setReclaim(false);
        setIsClaimButtonDisabled(false);
      }
    } catch (error) {
      showToast(format(texts.claimFailed, { error }), { error: true });
    } finally {
      setIsLoading(false);
    }
  }, [
    isComponentMountedRef,
    reclaim,
    activity,
    authUserName,
    showToast,
    logCoachingActivity,
    claimChannel,
    user,
    type,
  ]);

  const unclaimActivity = useCallback(async () => {
    try {
      if (isClaimedByUser && claimedFrom === ChannelClaimedFrom.CHAT) {
        showToast(texts.claimWarning.selfClaimedFromChannel, { error: true });
        return;
      }

      setIsClaimButtonDisabled(true);
      await unclaimChannel(user);
      showToast(texts.unclaimSuccessful);

      const [activityId] = activity.id;
      logCoachingActivity(CoachingActivity.UNCLAIMED_ACTIVITY, {
        activityId,
        activityType: type,
        clientId: user,
      });
      if (isComponentMountedRef.current) {
        setIsClaimButtonDisabled(false);
      }
    } catch (error) {
      showToast(format(texts.unclaimFailed, { error }), { error: true });
    }
  }, [
    isComponentMountedRef,
    activity,
    showToast,
    logCoachingActivity,
    isClaimedByUser,
    unclaimChannel,
    claimedFrom,
    user,
    type,
  ]);

  const onClaimClick = useCallback(async () => {
    // This activity has already been claimed by the logged in user
    if (isClaimedByUser) {
      showToast(texts.claimWarning.selfClaimed);
      return;
    }
    // Don't allow to claim an item if the user already has a claimed one
    if (hasClaimedOne) {
      showToast(
        format(texts.claimWarning.claimedActivitiesExist, { tab: sidebarTitles[type] }),
        { error: true },
      );
      return;
    }
    // If the activity was freshly claimed (within 30 minutes), we won't allow it be reclaimed
    if (!!claimedBy && !!claimedAt && moment().diff(moment(claimedAt), 'minutes') <= CLAIM_TIMEOUT_MINS) {
      showToast(texts.claimWarning.freshlyClaimed, { warning: true });
      return;
    }
    // Allow reclaiming after 30 minutes has passed after the previous claim, but only after showing a warning
    if (!!claimedBy && !!claimedAt && moment().diff(moment(claimedAt), 'minutes') > CLAIM_TIMEOUT_MINS) {
      setReclaim(true);
      return;
    }

    await claimActivity();
  }, [
    claimedAt,
    claimedBy,
    type,
    claimActivity,
    showToast,
    isClaimedByUser,
    hasClaimedOne,
  ]);

  return (
    <>
      <Tooltip
        title={isClaimedByUser ? texts.unclaimTooltip : texts.claimTooltip}
        placement="left-start"
        followCursor
      >
        <div>
          <ClaimButton
            onClick={isClaimedByUser ? unclaimActivity : onClaimClick}
            disabled={isClaimButtonDisabled}
          >
            {isClaimedByUser ? texts.UNCLAIM : texts.CLAIM}
            {isClaimButtonDisabled && <CircularProgressContainer size={20} />}
          </ClaimButton>
        </div>
      </Tooltip>
      <Modal
        title={texts.claimModalTitle}
        open={reclaim}
        onClose={() => setReclaim(false)}
        actionButtons={(
          <>
            <PrimaryButton
              onClick={claimActivity}
            >
              {texts.modalButton.OK}
            </PrimaryButton>
            <CancelButton onClick={() => setReclaim(false)}>{texts.modalButton.CANCEL}</CancelButton>
          </>
        )}
      >
        {format(texts.claimWarning.alreadyClaimed, { user: claimedByUserName, time: claimedTime })}
      </Modal>
      {isLoading && <LoadingOverlay isLoading={isLoading} />}
    </>
  );
};

ClaimActivity.propTypes = {
  activity: PropTypes.object.isRequired,
};

export default compose(
  observer,
)(ClaimActivity);
